import * as React from 'react';

import { enumUnit } from '@rematter/frontend-lib-api-client';
import {
  DataGridColumn,
  LocalizedDate,
  maybeEmptyInfo,
  MeasuredValue,
  Money,
  Tooltip,
} from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import {
  $columnDateRange,
  $columnSelectedStatuses,
  $columnSelectedSupplierIds,
} from '../../../_state/brokeredLoadsList.state';
import { useTranslations } from '../../../i18n/useTranslations';
import { BrokeredLoadRecord } from '../../../types/brokeredLoad';
import { BrokeredLoadActionMenu } from '../BrokeredLoadActionMenu';
import { DateRangeColumnFilter } from '../filters/DateRangeColumnFilter';
import { StatusColumnFilter } from '../filters/StatusColumnFilter';
import { SupplierColumnFilter } from '../filters/SupplierColumnFilter';

import { LoadStatusBadge } from '~/modules/trade/_components/_shared/StatusBadges';

interface CellLinkProps {
  children: React.ReactNode;
  onClick: (e: React.MouseEvent) => void;
}

const CellLink = ({ children, onClick }: CellLinkProps) => (
  <Tooltip tip={String(children)} className="@CellLink">
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick(e);
      }}
      className="@CellLink truncate text-left text-primary hover:underline"
    >
      {children}
    </button>
  </Tooltip>
);

type TruncatedCellProps = { children: React.ReactNode };

const TruncatedCell = ({ children }: TruncatedCellProps) => {
  if (children == null || children === '') {
    return null;
  }

  return (
    <Tooltip tip={String(children)} className="@TruncatedCell">
      <div className="@TruncatedCell truncate">{children}</div>
    </Tooltip>
  );
};

export const useBrokeredLoadsListColumns = ({
  onRefetch,
  onVoid,
  onDoubleClick,
  onMarkAsShipped,
  onMarkAsReceived,
  onBrokeredOrderClick,
  onSalesOrderClick,
}: {
  onClick?: (row: BrokeredLoadRecord) => void;
  onDoubleClick?: (row: BrokeredLoadRecord) => void;
  onRefetch?: () => void;
  onVoid?: (loadId: string) => void;
  onMarkAsShipped: (loadId: string) => void;
  onMarkAsReceived: (loadId: string) => void;
  onBrokeredOrderClick?: (brokeredOrderId: string) => void;
  onSalesOrderClick?: (salesOrderId: string) => void;
}) => {
  const t = useTranslations();

  const columnDateRange = useAtomValue($columnDateRange);
  const columnSelectedStatuses = useAtomValue($columnSelectedStatuses);
  const columnSelectedSupplierIds = useAtomValue($columnSelectedSupplierIds);

  const handleCellDoubleClick = React.useCallback(
    ({ row }: { row: BrokeredLoadRecord }) => {
      onDoubleClick?.(row);
    },
    [onDoubleClick],
  );

  const columns = React.useMemo(() => {
    const columnDefs: DataGridColumn<BrokeredLoadRecord>[] = [
      {
        key: 'expectedShipDate',
        header: t.brokerage.loads.table.columns.expectedShipDate,
        width: 140,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.expectedShipDate, (v) => (
            <LocalizedDate date={v} />
          )),
        columnFilter: ({ close }) => <DateRangeColumnFilter onClose={close} />,
        isFilterActive: !!columnDateRange,
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'loadNumber',
        header: t.brokerage.loads.table.columns.loadNumber,
        width: 100,
        cell: ({ row }) =>
          maybeEmptyInfo(row.loadNumber, (v) => (
            <TruncatedCell>{v}</TruncatedCell>
          )),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'supplier',
        header: t.brokerage.loads.table.columns.supplier,
        width: 180,
        cell: ({ row }) =>
          maybeEmptyInfo(row.supplier?.name, (v) => (
            <TruncatedCell>{v}</TruncatedCell>
          )),
        columnFilter: ({ close }) => <SupplierColumnFilter onClose={close} />,
        isFilterActive: columnSelectedSupplierIds.size > 0,
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'brokeredOrder',
        header: t.brokerage.loads.table.columns.brokeredOrder,
        width: 120,
        cell: ({ row }) =>
          maybeEmptyInfo(row.brokeredOrder?.orderNumber, (v) =>
            row.brokeredOrder?.id && onBrokeredOrderClick ? (
              <CellLink
                onClick={() =>
                  onBrokeredOrderClick(row.brokeredOrder?.id ?? '')
                }
              >
                {v}
              </CellLink>
            ) : (
              <TruncatedCell>{v}</TruncatedCell>
            ),
          ),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'salesOrderNumber',
        header: t.brokerage.loads.table.columns.salesOrderNumber,
        width: 140,
        cell: ({ row }) =>
          maybeEmptyInfo(row.salesOrderNumber, (v) =>
            row.salesOrderId && onSalesOrderClick ? (
              <CellLink
                onClick={() => onSalesOrderClick(row.salesOrderId ?? '')}
              >
                {v}
              </CellLink>
            ) : (
              <TruncatedCell>{v}</TruncatedCell>
            ),
          ),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'lineItemCount',
        header: t.brokerage.loads.table.columns.lineItemCount,
        width: 80,
        cell: ({ row }) => maybeEmptyInfo(row.lineItemCount),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'netQuantity',
        header: t.brokerage.loads.table.columns.netQuantity,
        width: 120,
        cell: ({ row }) =>
          maybeEmptyInfo(row.netQuantity, (v) => (
            <MeasuredValue value={v} unit={enumUnit.Pounds} />
          )),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'shippedTotal',
        header: t.brokerage.loads.table.columns.shippedTotal,
        width: 120,
        cell: ({ row }) =>
          maybeEmptyInfo(row.shippedTotal, (v) => (
            <Money amount={v} currencyCode="USD" />
          )),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'status',
        header: t.brokerage.loads.table.columns.status,
        width: 120,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.status, (v) => (
            <LoadStatusBadge status={v} isReviewed={row.reviewed} />
          )),
        columnFilter: ({ close }) => <StatusColumnFilter onClose={close} />,
        isFilterActive: columnSelectedStatuses.size > 0,
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'shippedDate',
        header: t.brokerage.loads.table.columns.shippedDate,
        width: 120,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.shippedDate, (v) => <LocalizedDate date={v} />),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'createdOn',
        header: t.brokerage.loads.table.columns.createdOn,
        width: 120,
        sortable: true,
        cell: ({ row }) =>
          maybeEmptyInfo(row.timestamps?.createdAt, (v) => (
            <LocalizedDate date={v} />
          )),
        onDoubleClick: handleCellDoubleClick,
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }) => (
          <BrokeredLoadActionMenu
            load={row}
            onRefetch={onRefetch}
            onVoid={onVoid}
            onMarkAsShipped={onMarkAsShipped}
            onMarkAsReceived={onMarkAsReceived}
          />
        ),
      },
    ];

    return columnDefs;
  }, [
    t,
    handleCellDoubleClick,
    onRefetch,
    onVoid,
    onMarkAsShipped,
    onMarkAsReceived,
    onBrokeredOrderClick,
    onSalesOrderClick,
    columnDateRange,
    columnSelectedStatuses,
    columnSelectedSupplierIds,
  ]);

  const columnVisibilityState = React.useMemo(
    () =>
      columns.reduce(
        (acc, column) => {
          acc[column.key] = true;
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    [columns],
  );

  return { columns, columnVisibilityState };
};
